import {
  CalendarDetailsVisibility,
  CalendarSyncEntry,
  ConnectionCalendar,
  EntityLinks,
  Goal,
  LifeArea,
  SyncMode,
} from '@griply/types';

import { getConnectionCalendar } from './get-connection-calendar';
import { resolveCalendarSyncTarget } from './resolve-calendar-sync-target';

export type GetSortedCalendarSyncsOptions = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  links: EntityLinks;
  calendars: ConnectionCalendar[];
  allTasksLabel: string;
};

// Ranked rather than compared on the enums' string values — the intended order is the one
// the selects render, which alphabetical comparison of the values only matches by accident.
const MODE_RANK: Record<SyncMode, number> = {
  [SyncMode.Automatic]: 0,
  [SyncMode.Manual]: 1,
};

const DETAILS_RANK: Record<CalendarDetailsVisibility, number> = {
  [CalendarDetailsVisibility.Visible]: 0,
  [CalendarDetailsVisibility.Hidden]: 1,
};

type SortableSync = {
  sync: CalendarSyncEntry;
  name: string;
  calendarName: string;
  modeRank: number;
  detailsRank: number;
};

// An empty name means the row renders a placeholder (target deleted, or calendar names not
// fetched yet), so it sinks — leading the list under a blank sort key reads as broken data.
const byName = (a: string, b: string) => {
  if (!a) {
    return b ? 1 : 0;
  }
  if (!b) {
    return -1;
  }
  return a.localeCompare(b);
};

// Ends on (connectionId, linkedItemId) so every comparison is decided: the pair is unique per
// sync, which keeps the order identical across engines instead of falling back to input order.
const bySortKeys = (a: SortableSync, b: SortableSync) =>
  byName(a.name, b.name) ||
  byName(a.calendarName, b.calendarName) ||
  a.modeRank - b.modeRank ||
  a.detailsRank - b.detailsRank ||
  a.sync.connectionId.localeCompare(b.sync.connectionId) ||
  a.sync.linkedItemId.localeCompare(b.sync.linkedItemId);

// Sort keys are resolved once per entry before sorting: resolveCalendarSyncTarget scans goals
// (250 at power-user scale), which inside a comparator would run O(n log n) times.
export const getSortedCalendarSyncs = (
  syncs: CalendarSyncEntry[],
  {
    goals,
    lifeAreas,
    links,
    calendars,
    allTasksLabel,
  }: GetSortedCalendarSyncsOptions,
): CalendarSyncEntry[] =>
  syncs
    .map((sync): SortableSync => {
      const target = resolveCalendarSyncTarget(sync.linkedItemId, {
        goals,
        lifeAreas,
        links,
      });
      const calendar = getConnectionCalendar(calendars, {
        connectionId: sync.connectionId,
        calendarId: sync.calendarId,
      });

      return {
        sync,
        name: target.isAllTasks ? allTasksLabel : target.name ?? '',
        calendarName: calendar?.name ?? '',
        modeRank: MODE_RANK[sync.mode],
        detailsRank:
          DETAILS_RANK[
            sync.detailsVisibility ?? CalendarDetailsVisibility.Visible
          ],
      };
    })
    .sort(bySortKeys)
    .map(({ sync }) => sync);
